import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  useTheme,
  useMediaQuery,
  Fade,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Settings as SettingsIcon,
  KeyboardArrowUp as ScrollTopIcon,
} from '@mui/icons-material';

const KNLSServices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [visibleCards, setVisibleCards] = useState({});
  const cardRefs = useRef({});

  // Make all categories visible immediately on mount
  useEffect(() => {
    const categoryIds = [
      'category-pan',
      'category-aadhar',
      'category-voter',
      'category-passport',
      'category-rta',
      'category-pf',
      'category-health',
      'category-print',
      'category-courses',
    ];
    categoryIds.forEach((categoryId) => {
      setVisibleCards((prev) => ({ ...prev, [categoryId]: true }));
    });
  }, []);

  useEffect(() => {
    document.title = 'KNLS Services - LOCSY';

    // Scroll to top button visibility
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);

    // Intersection Observer for scroll animations (only for individual service cards)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const timer = setTimeout(() => {
      const refs = Object.values(cardRefs.current);
      refs.forEach((ref) => {
        // Only observe service cards (not categories) for animation
        if (ref && ref.id && !ref.id.startsWith('category-')) {
          observer.observe(ref);
        }
      });
    }, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
      const refs = Object.values(cardRefs.current);
      refs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const servicesData = [
    {
      category: 'pan',
      categoryName: 'PAN Card Services',
      categoryIcon: '📋',
      type: 'government',
      gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
      services: [
        { name: 'Online Application', icon: '🆕', badge: 'Digital' },
        { name: 'Instant PAN with Father Name', icon: '👨‍👦', badge: 'Fast Service' },
        { name: 'Instant PAN without Father Name', icon: '📝', badge: 'Quick' },
        { name: 'Pan by Post', icon: '📮', badge: 'Delivery' },
        { name: 'Pan Update', icon: '🔄', badge: 'Modification' },
        { name: 'Pan No. Retrieval & Reissue', icon: '🔍', badge: 'Recovery' },
        { name: 'Pan Name Change', icon: '✏️', badge: 'Update' },
        { name: 'Minor Pan', icon: '👶', badge: 'Kids' },
      ],
    },
    {
      category: 'aadhar',
      categoryName: 'Aadhar Card Services',
      categoryIcon: '🆔',
      type: 'government',
      gradient: 'linear-gradient(135deg, #f093fb, #f5576c)',
      services: [
        { name: 'Aadhar Full Laminated and PVC', icon: '🖨️', badge: 'Premium' },
        { name: 'Aadhar Full Only Laminated', icon: '📄', badge: 'Standard' },
        { name: 'Aadhar PVC Card Site', icon: '💳', badge: 'Plastic Card' },
        { name: 'Aadhar Laminated Card Size', icon: '🎴', badge: 'Compact' },
        { name: 'Aadhar Update', icon: '🔄', badge: 'Online' },
        { name: 'Aadhar Mobile Number Verification', icon: '📱', badge: 'Verify' },
        { name: 'Aadhar Update History Color Print', icon: '🎨', badge: 'Color' },
        { name: 'Aadhar Update History BW Print', icon: '⚫', badge: 'B&W' },
        { name: 'Aadhar Update Status', icon: '📊', badge: 'Track' },
      ],
    },
    {
      category: 'voter',
      categoryName: 'Voter ID Services',
      categoryIcon: '🗳️',
      type: 'government',
      gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)',
      services: [
        { name: 'Form 6 - New', icon: '🆕', badge: 'Online' },
        { name: 'Form 8 - Update', icon: '🔄', badge: 'Online' },
        { name: 'Voter ID PVC (linked with mobile)', icon: '💳📱', badge: 'Print' },
        { name: 'Voter ID Laminated (linked with mobile)', icon: '📋📱', badge: 'Print' },
        { name: 'Voter ID PVC (not linked to mobile)', icon: '💳❌', badge: 'Print' },
        { name: 'Voter ID Laminated (linked with mobile)', icon: '📄📱', badge: 'Print' },
      ],
    },
    {
      category: 'passport',
      categoryName: 'Passport Services',
      categoryIcon: '🛂',
      type: 'government',
      gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)',
      services: [
        { name: 'Fresh (Single Slot Support)', icon: '🆕', badge: 'New' },
        { name: 'Fresh (Three Slot Support)', icon: '📦', badge: 'New' },
        { name: 'Renewal (Single Slot Support)', icon: '🔄', badge: 'Renew' },
        { name: 'Renewal (Three Slot Support)', icon: '🔄📦', badge: 'Renew' },
      ],
    },
    {
      category: 'rta',
      categoryName: 'Driving License & RTA Services',
      categoryIcon: '🚗',
      type: 'government',
      gradient: 'linear-gradient(135deg, #fa709a, #fee140)',
      services: [
        { name: 'Driving Licence', icon: '🪪', badge: 'Print' },
        { name: 'Driving Licence without OTP', icon: '📝', badge: 'Print' },
        { name: 'C Book', icon: '📖', badge: 'Print' },
        { name: 'C Book without Chasis & OTP', icon: '🚙', badge: 'Print' },
        { name: 'LLR MCWG', icon: '🏍️', badge: 'Learner' },
        { name: 'LLR MCWG AND LMV', icon: '🚛', badge: 'Learner' },
      ],
    },
    {
      category: 'pf',
      categoryName: 'PF Services',
      categoryIcon: '💰',
      type: 'government',
      gradient: 'linear-gradient(135deg, #00b4db, #0083b0)',
      services: [
        { name: 'Full Claim', icon: '💵', badge: 'Withdrawal' },
        { name: 'Nominee Update', icon: '👥', badge: 'Update' },
        { name: 'Pan & Aadhar KYC', icon: '🔐', badge: 'Verification' },
        { name: 'PF PVC Card', icon: '💳', badge: 'Print' },
      ],
    },
    {
      category: 'health',
      categoryName: 'Health & Social Services',
      categoryIcon: '🏥',
      type: 'government',
      gradient: 'linear-gradient(135deg, #30cfd0, #330867)',
      services: [
        { name: 'Sramik Card Online', icon: '👷', badge: 'Labour' },
        { name: 'Sramik PVC Card', icon: '💳', badge: 'Print' },
        { name: 'Sramik Laminated Card', icon: '📋', badge: 'Print' },
        { name: 'Ayushman Bharat Online', icon: '🏥', badge: 'Health' },
        { name: 'Ayushman Bharat Laminated', icon: '📄', badge: 'Print' },
        { name: 'Ayushman Bharat PVC', icon: '💳', badge: 'Print' },
        { name: 'EHS Cards - Retrieval & Activation', icon: '🏭', badge: 'Factory' },
        { name: 'EHS PVC Card', icon: '💳', badge: 'Print' },
        { name: 'EHS Laminated Card', icon: '📋', badge: 'Print' },
      ],
    },
    {
      category: 'print',
      categoryName: 'Printing & Document Services',
      categoryIcon: '🖨️',
      type: 'print',
      gradient: 'linear-gradient(135deg, #ff9a56, #ff6a88)',
      services: [
        { name: 'WhatsApp Prints - BW', icon: '⚫', badge: 'Black & White' },
        { name: 'WhatsApp Prints - BW Double Side', icon: '📄', badge: 'Duplex' },
        { name: 'WhatsApp Prints - Color', icon: '🌈', badge: 'Color' },
        { name: 'Xerox Single Side', icon: '📄', badge: 'Copy' },
        { name: 'Xerox Double Side', icon: '📑', badge: 'Duplex' },
        { name: 'Xerox Legal', icon: '⚖️', badge: 'A4' },
        { name: 'Xerox Legal Double', icon: '📜', badge: 'Legal Size' },
        { name: 'DTP Telugu', icon: '📰', badge: 'Language' },
        { name: 'DTP Excel', icon: '📊', badge: 'Spreadsheet' },
      ],
    },
    {
      category: 'courses',
      categoryName: 'Professional Courses',
      categoryIcon: '🎓',
      type: 'courses',
      gradient: 'linear-gradient(135deg, #a8edea, #fed6e3)',
      services: [
        { name: 'DCA Course', icon: '💻', badge: 'Certificate' },
        { name: 'ADCA Course', icon: '🖥️', badge: 'Advanced' },
        { name: 'DTP', icon: '🖨️', badge: 'Publishing' },
        { name: 'Tally', icon: '📈', badge: 'Accounting' },
        { name: 'Photo Designing', icon: '📸', badge: 'Creative' },
      ],
    },
  ];

  const filterButtons = [
    { key: 'all', label: 'All Services' },
    { key: 'government', label: 'Government Services' },
    { key: 'print', label: 'Print Services' },
    { key: 'courses', label: 'Courses' },
  ];

  const filterServices = () => {
    return servicesData.filter((category) => {
      if (activeFilter !== 'all' && category.type !== activeFilter) {
        return false;
      }
      if (searchQuery.trim() === '') {
        return true;
      }
      const query = searchQuery.toLowerCase();
      return (
        category.categoryName.toLowerCase().includes(query) ||
        category.services.some((service) => service.name.toLowerCase().includes(query))
      );
    }).map((category) => {
      if (searchQuery.trim() === '') {
        return category;
      }
      const query = searchQuery.toLowerCase();
      const filteredServices = category.services.filter((service) =>
        service.name.toLowerCase().includes(query)
      );
      return { ...category, services: filteredServices };
    }).filter((category) => category.services.length > 0);
  };

  const ServiceCard = ({ service, categoryGradient, category, index }) => {
    const cardId = `service-${category}-${index}`;
    const cardRef = useRef(null);

    useEffect(() => {
      if (cardRef.current) {
        cardRefs.current[cardId] = cardRef.current;
        // Make visible immediately
        setVisibleCards((prev) => ({ ...prev, [cardId]: true }));
      }
    }, [cardId]);

    return (
      <Box
        id={cardId}
        ref={cardRef}
        sx={{
          height: '100%',
          opacity: 1, // Always visible
        }}
      >
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-8px) scale(1.02)',
              boxShadow: 8,
              borderColor: 'primary.main',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent)',
              transition: 'left 0.5s ease',
            },
            '&:hover::before': {
              left: '100%',
            },
          }}
        >
          <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: isMobile ? 32 : 40,
                mb: 1.5,
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'rotate(360deg) scale(1.2)',
                },
              }}
            >
              {service.icon}
            </Typography>
            <Typography
              variant="h6"
              component="h4"
              fontWeight={600}
              sx={{ mb: 1, fontSize: isMobile ? '0.95rem' : '1rem' }}
            >
              {service.name}
            </Typography>
            <Chip
              label={service.badge}
              size="small"
              sx={{
                background: categoryGradient,
                color: 'white',
                fontWeight: 500,
                fontSize: '0.7rem',
                height: 24,
              }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  };

  const CategorySection = ({ category, index }) => {
    const categoryId = `category-${category.category}`;
    const categoryRef = useRef(null);

    useEffect(() => {
      if (categoryRef.current) {
        cardRefs.current[categoryId] = categoryRef.current;
      }
    }, [categoryId]);

    return (
      <Box
        id={categoryId}
        ref={categoryRef}
        sx={{
          mb: 6,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2.5,
            borderRadius: 3,
            background: category.gradient,
            color: 'white',
            boxShadow: 4,
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: 8,
            },
          }}
        >
          <Typography sx={{ fontSize: isMobile ? 32 : 40 }}>{category.categoryIcon}</Typography>
          <Typography variant="h5" component="h2" fontWeight={600} sx={{ fontSize: isMobile ? '1.3rem' : '1.75rem' }}>
            {category.categoryName}
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {category.services.map((service, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
              <ServiceCard
                service={service}
                categoryGradient={category.gradient}
                category={category.category}
                index={idx}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const filteredCategories = filterServices();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 2 : 3 }}>
        {/* Hero Section */}
        <Fade in timeout={600}>
          <Box
            sx={{
              mb: 4,
              textAlign: 'center',
              p: isMobile ? 3 : 5,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: 6,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              component="h1"
              gutterBottom
              fontWeight={700}
              sx={{ fontSize: isMobile ? '1.5rem' : '2rem', position: 'relative', zIndex: 1 }}
            >
              Complete Digital Services Solution
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.95,
                fontSize: isMobile ? '0.9rem' : '1rem',
                position: 'relative',
                zIndex: 1,
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              Your one-stop destination for all government document services, printing, and professional courses
            </Typography>
            <Chip
              label="✨ 64+ Services Available"
              sx={{
                mt: 2,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                fontSize: '0.875rem',
              }}
            />
          </Box>
        </Fade>

        {/* Search Bar */}
        <Box sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
          <TextField
            fullWidth
            placeholder="Search for services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 6,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
                '&.Mui-focused': {
                  boxShadow: 6,
                },
              },
            }}
          />
        </Box>

        {/* Filter Buttons */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
          {filterButtons.map((btn) => (
            <Button
              key={btn.key}
              variant={activeFilter === btn.key ? 'contained' : 'outlined'}
              onClick={() => setActiveFilter(btn.key)}
              sx={{
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                transition: 'all 0.3s',
                ...(activeFilter === btn.key && {
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  },
                }),
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Box>

        {/* Services Sections */}
        {filteredCategories.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              😕 No services found matching your search
            </Typography>
          </Box>
        ) : (
          filteredCategories.map((category, index) => (
            <CategorySection key={category.category} category={category} index={index} />
          ))
        )}

        {/* Footer */}
        <Box sx={{ mt: 6, textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            &copy; {new Date().getFullYear()} Digital Services Hub. All rights reserved.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Providing quality digital services with excellence
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            <Box
              component="a"
              href="/privacy-policy"
              sx={{ color: 'primary.main', textDecoration: 'underline', mr: 2 }}
            >
              Privacy Policy
            </Box>
            <Box
              component="a"
              href="/terms-and-conditions"
              sx={{ color: 'primary.main', textDecoration: 'underline' }}
            >
              Terms & Conditions
            </Box>
          </Typography>
        </Box>
      </Container>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 30,
            right: 30,
            bgcolor: 'primary.main',
            color: 'white',
            width: 50,
            height: 50,
            boxShadow: 4,
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'translateY(-5px)',
              boxShadow: 8,
            },
            zIndex: 1000,
          }}
        >
          <ScrollTopIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default KNLSServices;
